// IMPORTANT: THis file is used on frontend and backend, so do not include
// things here that do not need to be part of the frontend. Instead, use
// the front-end/back-end specific utils files.
import _ from 'lodash';
import type Joi from 'joi';
import moment from 'moment/moment';
import { getGameConfig } from '../gameConfig';
import { RaidDifficulty } from '../gameConfig/sharedTypes';

export function safeCharacterName(str: string) {
  return safeCapitalize(str.split('-', 2)[0]);
}

// essentially an alias for _.isObject but works around a typing
// issue where objects that you validate with _.isObject become
// lowercase 'o' objects, and therefore cannot have property indexes
// see here for details: https://stackoverflow.com/a/63456317
export function isObject(obj: any) {
  return _.isObject(obj);
}

// Like an assert but throws an exception if the condition does not pass
// This is NOT compiled out, so you can have side effects in the condition.
export function ensure<T>(condition: T, exceptionMessage: string, errorClass: any = Error) {
  if (!condition) {
    throw new errorClass(exceptionMessage);
  }
  return condition!;
}

// Used in filter to remove undefined/null values
// Used like .filter(filterNotEmpty)
export function filterNotEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

const DEFAULT_OMIT_VALUES: unknown[] = [null, undefined, ''];

// Returns a shallow copy of `values` with keys removed whose value matches any
// entry in `omitValues`. Defaults to dropping null, undefined, and empty string.
export function omitValues<T extends Record<string, any>>(values: T, valuesToOmit?: unknown[]): Partial<T> {
  const drop = valuesToOmit ?? DEFAULT_OMIT_VALUES;
  const result: Partial<T> = {};
  for (const key in values) {
    if (!drop.includes(values[key])) {
      result[key] = values[key];
    }
  }
  return result;
}

export type TSafeSetIntervalReturn = {
  timeout: NodeJS.Timeout | null;
  clear: () => void;
};

export function safeSetInterval(callback: any, ms: number) {
  let cleared = false;
  const returnValue: TSafeSetIntervalReturn = {
    timeout: null,
    clear: () => {
      cleared = true;
      if (returnValue.timeout) {
        clearTimeout(returnValue.timeout);
        returnValue.timeout = null;
      }
    }
  };

  async function timeoutFunction() {
    try {
      await callback();
    } finally {
      if (!cleared) {
        returnValue.timeout = setTimeout(() => timeoutFunction(), ms);
      }
    }
  }

  timeoutFunction();

  return returnValue;
}

// capitalizes the word in a safe manner, because a regular _.capitalize() will
// cause letters like 'ß' to be converted to 'SS'
export function safeCapitalize(str: string) {
  const firstCharacter = str.charAt(0);

  let capitalizedFirstCharacter = firstCharacter.toUpperCase();
  if (capitalizedFirstCharacter.length > 1) {
    capitalizedFirstCharacter = firstCharacter;
  }

  return capitalizedFirstCharacter + str.substring(1).toLowerCase();
}

export function buildTwitchProfileUrl(name: string) {
  if (!name) {
    return '';
  }
  return `https://twitch.tv/${encodeURIComponent(name)}`;
}

export function buildYouTubeProfileUrl(name: string) {
  if (!name) {
    return '';
  }

  // regex found here: https://webapps.stackexchange.com/a/101153
  if (name.match(/^UC[0-9A-Za-z_-]{21}[AQgw]$/)) {
    return `https://youtube.com/channel/${encodeURIComponent(name)}`;
    // youtube allows custom handles which will always start with an @.
  } else if (name.charAt(0) === '@') {
    return `https://youtube.com/@${encodeURIComponent(name.substr(1))}`;
  } else {
    return `https://youtube.com/user/${encodeURIComponent(name)}`;
  }
}

export function buildTwitterProfileUrl(name: string) {
  if (!name) {
    return '';
  }
  return `https://twitter.com/${encodeURIComponent(name)}`;
}

export function uniqueValues(list: string[]) {
  const dict: any = {};
  list.forEach((element: string) => (dict[element] = true));
  return Object.keys(dict);
}

export function safeParseJson<T = any>(str: string, schema?: Joi.ObjectSchema<T> | Joi.ArraySchema<T>): T | null;
export function safeParseJson<T = any>(
  str: string,
  schema: Joi.ObjectSchema<T> | Joi.ArraySchema<T> | undefined,
  errorReturn: T
): T;
export function safeParseJson<T = any>(
  str: string,
  schema?: Joi.ObjectSchema<T> | Joi.ArraySchema<T>,
  errorReturn?: T
): T | null {
  try {
    const res = JSON.parse(str);

    if (schema) {
      const { error, value } = schema.validate(res);
      if (error) {
        throw error;
      }
      return value;
    }

    return res;
  } catch (e) {
    return errorReturn !== undefined ? errorReturn : null;
  }
}

export function safeParseInt(str: string, defaultValue?: number) {
  const res = parseInt(str, 10);
  if (isNaN(res)) {
    return defaultValue || 0;
  }
  return res;
}

export function safeParseFloat(str: string, defaultValue?: number) {
  const res = parseFloat(str);
  if (isNaN(res)) {
    return defaultValue || 0;
  }
  return res;
}

export function safeParseIntOrUndefined(str: string, defaultValue?: number | undefined) {
  const res = parseInt(str, 10);
  if (isNaN(res)) {
    return defaultValue;
  }
  return res;
}

export const safeParseIntOrDefault = <DefaultType = undefined>(
  str: string | number | null | undefined,
  defaultValue: DefaultType = undefined as DefaultType
): number | DefaultType => {
  if (str === null || str === undefined) {
    return defaultValue;
  }

  if (typeof str === 'number') {
    if (isNaN(str)) {
      return defaultValue;
    }
    return str;
  }

  const res = parseInt(str, 10);

  if (isNaN(res)) {
    return defaultValue;
  }
  return res;
};

export const safeParseFloatOrDefault = <DefaultType>(
  str: string | number | null | undefined,
  defaultValue: DefaultType
): number | DefaultType => {
  if (str === null || str === undefined) {
    return defaultValue;
  }

  if (typeof str === 'number') {
    if (isNaN(str)) {
      return defaultValue;
    }
    return str;
  }

  const res = parseFloat(str);

  if (isNaN(res)) {
    return defaultValue;
  }

  return res;
};

export function safeParseIntCSV(str: string, defaults: number[] = []) {
  if (str === undefined) {
    return defaults;
  }
  const results: number[] = [];
  for (const part of str.split(',')) {
    const value = parseInt(part, 10);
    if (value !== undefined && !isNaN(value)) {
      results.push(value);
    }
  }
  return results;
}

export function stripDashes(str: string) {
  return str.replace(/-/g, '');
}

export const delayForMs = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new Error('Aborted'));
      });
    }
  });

/**
 * @param patch either x.x.x or x.x
 */
export function convertPatchStringToNumber(patch: string): number {
  const patchSemver = patch.split('.');

  const majorVersion = safeParseInt(patchSemver[0], 0);
  const minorVersion = safeParseInt(patchSemver[1], 0);
  const patchVersion = safeParseInt(patchSemver[2], 0);

  if (minorVersion >= 10 || patchVersion >= 10) {
    throw new Error(`Version minor or patch is over 10 and is not supported to be converted as number: ${patch}`);
  }

  let patchNumber = 0;

  patchNumber += majorVersion * 100;
  patchNumber += minorVersion * 10;
  patchNumber += patchVersion;

  return patchNumber;
}

/*
 * @param input
 * Remove whitespace, linebreak, as well as the ZWSP character
 */
export function safeTrim(input: string): string {
  if (!input) {
    return '';
  }

  if (input.match(/^[\s\u200B]+$/)) {
    return '';
  }

  return input.trim();
}

export function roundScore(score: number, precision: number = 1) {
  return parseFloat(score.toFixed(precision));
}

export function getBestPrecision(frac: number) {
  const MAX_PRECISION = 5;
  let precision = 2;
  for (; precision < MAX_PRECISION; ++precision) {
    const val = frac.toFixed(precision);
    if (val.match(/[1-9]/)) {
      return val;
    }
  }
  return frac.toFixed(MAX_PRECISION - 1);
}

type TItemLink = {
  color: string;
  itemId: number;
  enchantId: number | null;
  gemIds: number[];
  bonusIds: number[];
};

export function parseItemLink(itemLink: string): TItemLink | null {
  const res: any = {};

  const linkParts = itemLink.split('|');

  res.color = linkParts[1].slice(1);

  const itemStringRaw = linkParts[2];
  if (!itemStringRaw.startsWith('H')) {
    return null;
  }

  const itemParts = itemStringRaw.slice(1).split(':');

  res.itemId = safeParseInt(itemParts[1]);
  res.enchantId = safeParseInt(itemParts[2]) || null;

  res.gemIds = [
    safeParseInt(itemParts[3]),
    safeParseInt(itemParts[4]),
    safeParseInt(itemParts[5]),
    safeParseInt(itemParts[6])
  ].filter(Boolean);

  const numBonus = safeParseInt(itemParts[13]);
  res.bonusIds = [];
  for (let i = 0; i < numBonus; i++) {
    const bonusId = safeParseInt(itemParts[14 + i]);
    if (bonusId) {
      res.bonusIds.push(bonusId);
    }
  }

  return res;
}

export function calcPageForOffset(offset: number, pageSize: number = 20) {
  return Math.ceil(offset / pageSize) - 1;
}

// like lodash's _.groupBy() but doesn't do field lookup and expects the lookup to be unique, so only use for simple groupBys
export function groupByDictUnique<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL } {
  return list.reduce(
    (accum, item) => {
      accum[(item as any)[field]] = item;
      return accum;
    },
    {} as { [key: string]: TVAL }
  );
}

// like lodash's _.groupBy() but does do field lookup and expects the lookup to be unique, so only use for simple groupBys
// will be slower than groupByDictUnique()
export function groupByDictUniqueWithFieldLookup<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL } {
  return list.reduce(
    (accum, item) => {
      accum[_.get(item, field)] = item;
      return accum;
    },
    {} as { [key: string]: TVAL }
  );
}

// like lodash's _.groupBy() but doesn't do field lookup, so only use for simple groupBys
export function groupByDictList<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL[] } {
  return list.reduce(
    (accum, item) => {
      const key = (item as any)[field];
      const val = accum[key];
      if (val) {
        val.push(item);
      } else {
        accum[key] = [item];
      }
      return accum;
    },
    {} as { [key: string]: TVAL[] }
  );
}

export function uniqBy<T>(array: T[], comparator?: (value: T) => string): T[] {
  if (!comparator) {
    return Array.from(new Set(array));
  }

  const matchLookup = new Set<string>();
  const uniqueArray: T[] = [];
  for (const item of array) {
    const computed = comparator(item);
    if (matchLookup.has(computed)) {
      continue;
    }

    matchLookup.add(computed);
    uniqueArray.push(item);
  }

  return uniqueArray;
}

// Alternative to Array.from specifically for sets
export function arrayFromSet<T>(set: Set<T>): T[] {
  const results: T[] = [];
  set.forEach((x: T) => results.push(x));
  return results;
}

export function approxEq(v1: number, v2: number, epsilon = 0.001) {
  return Math.abs(v1 - v2) <= epsilon;
}

export function buildWeightedList(kvStrings: string[]) {
  const parts: string[] = [];
  for (const str of kvStrings) {
    const [v, count] = str.split(':');
    const n = safeParseInt(count, 1); // fall back to 1 sample if there's no valid number
    for (let i = 0; i < n; ++i) {
      parts.push(v);
    }
  }
  return parts;
}

export const truncateText = (text: string, limit: number) => {
  if (text.length > limit) {
    let subText = text.substring(0, limit);
    const lastSpace = subText.lastIndexOf(' ');
    if (lastSpace !== -1) {
      subText = subText.substring(0, lastSpace);
    }
    return subText + '…';
  } else {
    return text;
  }
};

export const getErrorMessage = (error: unknown, defaultErrorMessage: string = 'An unknown error occurred') => {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return defaultErrorMessage;
};

const DEFAULT_PERIOD_EPOCHS: { [key: string]: any } = {
  us: moment.utc('2005-12-27T15:00:00'),
  eu: moment.utc('2005-12-28T07:00:00'),
  tw: moment.utc('2005-12-28T23:00:00'),
  kr: moment.utc('2005-12-28T23:00:00'),
  cn: moment.utc('2005-12-28T23:00:00')
};

// Blizzard changed the reset time on November 16th:
//
// Weekly Reset Time Changing to 05:00 CET on 16 November
//
// https://eu.forums.blizzard.com/en/wow/t/weekly-reset-time-changing-to-0500-cet-on-16-november/398498

// the date at which the EU epoch change kicks in
const EU_EPOCH_CHANGE_STARTED_AT_DATE = moment.utc('2022-11-16T04:00:00');
// the base date we return when requesting a date using the new epoch
const EU_EPOCH_CHANGE_DATE = moment.utc('2005-12-28T04:00:00');

// the period that the EU epoch change started at
const EU_EPOCH_CHANGE_PERIOD = 881;

export function getRegionEpochByDate(userRegion: string, userDate: any) {
  const region = userRegion.toLocaleLowerCase();
  const when = moment.utc(userDate);
  if (region === 'eu' && when >= EU_EPOCH_CHANGE_STARTED_AT_DATE) {
    return moment.utc(EU_EPOCH_CHANGE_DATE);
  }
  return moment.utc(DEFAULT_PERIOD_EPOCHS[region]);
}

export function getRegionEpochByPeriod(userRegion: string, period: number) {
  const region = userRegion.toLocaleLowerCase();
  if (region === 'eu' && period >= EU_EPOCH_CHANGE_PERIOD) {
    return moment.utc(EU_EPOCH_CHANGE_DATE);
  }
  return moment.utc(DEFAULT_PERIOD_EPOCHS[region]);
}

export function getRegionalRaidWeek(regionSlug: string, date: any): number {
  const epoch = getRegionEpochByDate(regionSlug, date);
  if (!epoch) {
    throw new Error(`could not find regional slug in epochs list: ${regionSlug}`);
  }
  return moment.utc(date).diff(epoch, 'weeks');
}

export function getKeystoneLeaderboardPeriod(regionSlug: string, userDate: any): number {
  const when = moment.utc(userDate);
  const epoch = getRegionEpochByDate(regionSlug, when);
  if (!epoch) {
    throw new Error(`could not find regional slug in epochs list: ${regionSlug}`);
  }
  return when.diff(epoch, 'weeks');
}

// returns the start/end datetimes (as moments) for a given period and region
export function getKeystoneLeaderboardPeriodDates(regionSlug: string, period: number) {
  const startDate = moment(getRegionEpochByPeriod(regionSlug, period)).add(period, 'weeks');
  const endDate = moment(startDate).add(1, 'week');
  return [startDate, endDate];
}

export type TWeeklyResetWindowPhase = 'outside_window' | 'approaching_reset' | 'post_reset';

export type TWeeklyResetWindowState = {
  regionSlug: string;
  resetRegionSlug: string;
  phase: TWeeklyResetWindowPhase;
  isActive: boolean;
  previousResetAt: Date;
  resetAt: Date;
  nextResetAt: Date;
  focusResetStartAt: Date;
  focusResetEndAt: Date;
  windowStartAt: Date;
  windowEndAt: Date;
  hoursUntilReset: number;
  hoursSinceReset: number;
};

export function getResetRegionSlug(regionSlug: string) {
  const normalizedRegion = String(regionSlug || '').toLowerCase();
  if (normalizedRegion === 'tr') {
    return 'eu';
  }
  if (normalizedRegion === 'cn') {
    return 'tw';
  }
  return normalizedRegion;
}

export function getWeeklyResetWindowState(
  regionSlug: string,
  userDate: any,
  options: { leadUpHours: number; postResetHours: number }
): TWeeklyResetWindowState {
  const resetRegionSlug = getResetRegionSlug(regionSlug);
  const currentDate = moment.utc(userDate);
  const currentPeriod = getKeystoneLeaderboardPeriod(resetRegionSlug, currentDate);
  const [currentResetStartAt, nextResetAt] = getKeystoneLeaderboardPeriodDates(resetRegionSlug, currentPeriod);
  const previousResetAt = currentResetStartAt.clone().subtract(1, 'week');
  const approachingWindowStartAt = nextResetAt.clone().subtract(options.leadUpHours, 'hours');
  const postResetWindowEndAt = currentResetStartAt.clone().add(options.postResetHours, 'hours');

  if (currentDate.isSameOrAfter(currentResetStartAt) && currentDate.isBefore(postResetWindowEndAt)) {
    return {
      regionSlug,
      resetRegionSlug,
      phase: 'post_reset',
      isActive: true,
      previousResetAt: previousResetAt.toDate(),
      resetAt: currentResetStartAt.toDate(),
      nextResetAt: nextResetAt.toDate(),
      focusResetStartAt: previousResetAt.toDate(),
      focusResetEndAt: currentResetStartAt.toDate(),
      windowStartAt: currentResetStartAt.toDate(),
      windowEndAt: postResetWindowEndAt.toDate(),
      hoursUntilReset: 0,
      hoursSinceReset: moment.duration(currentDate.diff(currentResetStartAt)).asHours()
    };
  }

  if (currentDate.isSameOrAfter(approachingWindowStartAt) && currentDate.isBefore(nextResetAt)) {
    return {
      regionSlug,
      resetRegionSlug,
      phase: 'approaching_reset',
      isActive: true,
      previousResetAt: currentResetStartAt.toDate(),
      resetAt: nextResetAt.toDate(),
      nextResetAt: nextResetAt.toDate(),
      focusResetStartAt: currentResetStartAt.toDate(),
      focusResetEndAt: nextResetAt.toDate(),
      windowStartAt: approachingWindowStartAt.toDate(),
      windowEndAt: nextResetAt.toDate(),
      hoursUntilReset: moment.duration(nextResetAt.diff(currentDate)).asHours(),
      hoursSinceReset: Math.max(0, moment.duration(currentDate.diff(currentResetStartAt)).asHours())
    };
  }

  return {
    regionSlug,
    resetRegionSlug,
    phase: 'outside_window',
    isActive: false,
    previousResetAt: currentResetStartAt.toDate(),
    resetAt: nextResetAt.toDate(),
    nextResetAt: nextResetAt.toDate(),
    focusResetStartAt: currentResetStartAt.toDate(),
    focusResetEndAt: nextResetAt.toDate(),
    windowStartAt: approachingWindowStartAt.toDate(),
    windowEndAt: postResetWindowEndAt.toDate(),
    hoursUntilReset: moment.duration(nextResetAt.diff(currentDate)).asHours(),
    hoursSinceReset: Math.max(0, moment.duration(currentDate.diff(currentResetStartAt)).asHours())
  };
}

export function getRegionalRaidWeekStart(regionSlug: string, date: any): any {
  const raidWeek = getRegionalRaidWeek(regionSlug, date);
  const epoch = getRegionEpochByDate(regionSlug, date);
  return moment.utc(epoch).add(raidWeek, 'weeks');
}

export function getRegionalRaidWeekEnd(regionSlug: string, date: any): any {
  const raidWeek = getRegionalRaidWeek(regionSlug, date);
  const epoch = getRegionEpochByDate(regionSlug, date);
  return moment
    .utc(epoch)
    .add(raidWeek + 1, 'weeks')
    .subtract(1, 'second');
}

export function getRaidDifficultyReleaseDateForRegion(raidSlug: string, regionSlug: string, difficulty: string) {
  const raid = getGameConfig().RaidsBySlug[raidSlug];
  if (!raid) {
    return null;
  }

  const difficultyStart = raid.difficultyStarts?.[difficulty]?.[regionSlug];
  if (difficultyStart) {
    return moment.utc(difficultyStart);
  }

  const baseStart = raid.starts?.[regionSlug];
  if (!baseStart) {
    return null;
  }

  if (difficulty === RaidDifficulty.MYTHIC && raid.supportedDifficulties.includes(RaidDifficulty.MYTHIC)) {
    return moment.utc(baseStart).add(1, 'week');
  }

  return moment.utc(baseStart);
}

export function getRaidWeekForRaidAndRegion(raidSlug: string, regionSlug: string, difficulty: string, userDate: any) {
  if (!userDate) {
    return null;
  }

  const momentDate = moment.utc(userDate);
  if (!momentDate.isValid()) {
    return null;
  }

  const releaseDate = getRaidDifficultyReleaseDateForRegion(raidSlug, regionSlug, difficulty);
  if (!releaseDate) {
    return null;
  }

  const baseRaidWeek = getRegionalRaidWeek(regionSlug, releaseDate);
  const userRaidWeek = getRegionalRaidWeek(regionSlug, momentDate);
  return userRaidWeek - baseRaidWeek + 1;
}

type TRaidWeeklyRotationStatusParams = {
  regionSlug: string;
  rotationStartDate: moment.Moment;
  rotationEndDate: moment.Moment;
  alwaysActiveEndDate: moment.Moment;
  isAlwaysActiveAfterRotation: boolean;
  rotationWeeks: number;
  rotationWeeksActive: number;
  currentDate: moment.Moment;
};
type TRaidWeeklyRotationStatusResults = {
  weeksUntilActive: number;
  nextActivePeriod: number;
  isActive: boolean;
};

export function getRaidWeeklyRotationStatus(params: TRaidWeeklyRotationStatusParams): TRaidWeeklyRotationStatusResults {
  const {
    regionSlug,
    currentDate,
    rotationStartDate,
    rotationEndDate,
    rotationWeeks,
    rotationWeeksActive,
    isAlwaysActiveAfterRotation,
    alwaysActiveEndDate
  } = params;

  if (currentDate >= alwaysActiveEndDate) {
    return {
      weeksUntilActive: 0,
      nextActivePeriod: 0,
      isActive: false
    };
  }

  const currentPeriod = getKeystoneLeaderboardPeriod(regionSlug, currentDate);
  if (currentDate >= rotationEndDate) {
    return {
      weeksUntilActive: 0,
      nextActivePeriod: currentPeriod,
      isActive: isAlwaysActiveAfterRotation
    };
  }

  const startPeriod = getKeystoneLeaderboardPeriod(regionSlug, rotationStartDate);

  const endPeriod = getKeystoneLeaderboardPeriod(regionSlug, rotationEndDate);

  const cyclesSinceStart = Math.floor((currentPeriod - startPeriod) / rotationWeeks);
  let nextCycleStart = startPeriod + cyclesSinceStart * rotationWeeks + rotationWeeks;

  if (isAlwaysActiveAfterRotation && nextCycleStart >= endPeriod) {
    nextCycleStart = endPeriod;
  }

  const isActive =
    currentPeriod >= startPeriod &&
    currentPeriod < startPeriod + rotationWeeksActive + cyclesSinceStart * rotationWeeks;

  const weeksUntilActive = isActive ? 0 : nextCycleStart - currentPeriod;

  return {
    weeksUntilActive,
    isActive,
    nextActivePeriod: currentPeriod + weeksUntilActive
  };
}

export function getTournamentRealmLeaderboardPeriod(date: Date = new Date()): number {
  // the tournament realm is on a different set of periods than real realms, here is the offset
  const TOURNAMENT_REALM_PERIOD_DELTA = -741;
  // TR always acts like EU region for reset times
  return getKeystoneLeaderboardPeriod('eu', date) + TOURNAMENT_REALM_PERIOD_DELTA;
}

export function utcOrNull(date: string) {
  return date ? moment.utc(date).toDate() : null;
}

// Formats a guild name for display including the alt name from group customization if it exists.
export function formatGuildName(name: string, altName?: string): string {
  return altName && altName.length > 0 ? `${name} (${altName})` : name;
}

// Formats a realm name for display. Realms on non-English locales carry a native-language name in
// `altName` (Chinese for CN/TW, Korean for KR, Russian for the RU realms in EU); lead with the name in
// the realm's own language and keep the other in parens. English realms invert, so the primary name is
// always the one that matches the realm's locale.
export function formatRealmDisplayName(realm: { name: string; altName?: string | null; locale?: string }): string {
  if (!realm.altName || realm.altName === realm.name) {
    return realm.name;
  }

  return realm.locale?.startsWith('en') ? `${realm.name} (${realm.altName})` : `${realm.altName} (${realm.name})`;
}

/**
 * Parsed result from a featured streamer entry token.
 * Tokens come from the pipe-delimited `user=`, `game=`, or numeric ID strings
 * stored in the `{twitch}:featured-user-ids` Redis list.
 */
export type TFeaturedEntryToken =
  | { type: 'user'; username: string; isForced: boolean }
  | { type: 'game'; gameId: string; count: number }
  | { type: 'id'; id: number; isForced: boolean };

/**
 * Parse a single token from the featured streamer entry format.
 *
 * Supported formats:
 *   - `user=twitchname` or `user=twitchname!` (force-show)
 *   - `game=gameId` or `game=gameId:count`
 *   - `12345` or `12345!` (force-show) or `-1` (monitored stream synthetic ID)
 */
export function parseFeaturedEntryToken(raw: string): TFeaturedEntryToken | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('user=')) {
    const value = trimmed.slice(5);
    const isForced = value.endsWith('!');
    const username = isForced ? value.slice(0, -1) : value;
    return username ? { type: 'user', username, isForced } : null;
  }

  if (trimmed.startsWith('game=')) {
    const value = trimmed.slice(5);
    const colonIndex = value.indexOf(':');
    const gameId = colonIndex === -1 ? value : value.slice(0, colonIndex);
    const count = colonIndex === -1 ? 1 : safeParseInt(value.slice(colonIndex + 1), 1);
    return gameId ? { type: 'game', gameId, count } : null;
  }

  return { type: 'id', id: safeParseInt(trimmed, 0), isForced: trimmed.endsWith('!') };
}
